<script setup>
import { get, useStorage } from '@vueuse/core'
import Ob from '~/components/futures/icons/Ob.vue'
import Arrow from '~/components/futures/icons/Arrow.vue'
import { useSpotStore } from '~/store/spot'
import { add, fixRaw, getPrecision } from '#imports'
import ws, { orderbook, resetOrderbook, trades } from '~/apis/spotSocket'
import { usePanelStore } from '~/store/spot/panel'
import Mod from './Mod.vue'
import Decimal from 'decimal.js-light'

const props = defineProps({
  ob: {
    type: Number,
    default: 0,
  },
})

const spot = useSpotStore()
const panel = usePanelStore()
const obKey = useStorage('spot_obkey', 0)

const obs = computed(() => [
  { buy: true, sell: true, key: 0 },
  { buy: true, key: 1 },
  { sell: true, key: -1 },
])
const steps = computed(() => spot.steps?.map(i => ({
  label: Number.isNaN(+i) ? i : fix(i, getPrecision(i)),
  value: i,
  key: i,
})))

const n = computed(() => {
  const step = +spot.step || 0
  const [,size] = step.toExponential().split('e') ?? ['', 0]
  return +size > 0 ? 0 : -size
})

const maxLength = computed(() => props.ob ? 12 : 20)
const rateClass = computed(() => ({ 'text-rise': spot.overview?.r > 0, 'text-fall': spot.overview?.r < 0 }))
const priceText = computed(() => fix(spot.overview?.c, spot.ps))

const map = computed(() => ({
  asks: {
    '0': maxLength.value / 2,
    '1': 0,
    '-1': maxLength.value,
  },
  bids: {
    '0': maxLength.value / 2,
    '1': maxLength.value,
    '-1': 0,
  },
}))

const asks = computed(() => {
  const o = get(orderbook)
  if (o)
    return formatData({ arr: o.asks || [], maxlen: map.value.asks[get(obKey)], code: o.code, isAsks: true })
  return {}
})

const bids = computed(() => {
  const o = get(orderbook)

  if (o)
    return formatData({ arr: o.bids || [], maxlen: map.value.bids[get(obKey)], code: o.code })
  return {}
})

const ordersByLevel = computed(() => {
  if (!spot.step)
    return {}
  const o = spot.orderByCode[spot.code] ?? []
  return o.reduce((sofar, next) => {
    const level = new Decimal(next.ordPrice).idiv(spot.step).mul(spot.step)
    sofar[level] ??= []
    sofar[level].push(next)

    return sofar
  }, {})
})

watch([() => spot.id, () => spot.step], ([nId, np], [oId, op]) => {
  if (nId !== oId || np !== op) {
    nId && np && getData(nId, np)
    oId && op && unsub(oId, op)
  }
}, { immediate: true })

function formatData({ arr = [], maxlen = maxLength.value, isAsks, code }) {
  if (maxlen === 0 || code !== spot.code)
    arr = []

  const res = arr.reduce((sofar, cur, idx) => {
    const [price, qty] = cur
    const amount = mul(price, qty)
    sofar.max = Math.max(sofar.max, qty)
    sofar.t = add(sofar.t, qty)
    sofar.ta = add(sofar.ta, amount)
    const ap = div(sofar.ta, sofar.t) // 平均价
    const mark = ordersByLevel.value[price]?.length > 0

    if (idx < maxlen)
      sofar.total = add(sofar.total, qty)

    sofar.list[idx] = {
      id: price,
      p: price,
      a: amount,
      t: sofar.t,
      ta: sofar.ta,
      empty: false,
      amount,
      ap,
      qty,
      mark,
    }
    return sofar
  }, {
    list: Array.from({ length: maxLength.value }).fill('').map((_, j) => ({ id: `idx_${j}`, p: '---', qty: '---', a: '---', t: '---', amount: 0, empty: true })),
    max: 0,
    t: 0, // 累计成交量
    ta: 0, // 累计成交额
    total: 0,
  })
  res.list = res.list.slice(0, maxlen)
  if (isAsks)
    res.list.reverse()

  const first = isAsks ? res.list.slice(-1) : res.list
  if (first.length) {
    const [{ p, empty }] = first
    if (!empty) {
      if (isAsks)
        spot.bbo.a1 = p
      else
        spot.bbo.b1 = p

      spot.bbo.code = get(orderbook).code
    }
  }
  return res
}

// 根据盘口价格获取当前订单
// function order() {

// }

function getData(id = spot.id, step = spot.step) {
  // resetOrderbook()
  ws.subscribe(`${id}@snapshot_depth@20@${step}`)
  spot.bbo = {}
}

function unsub(id = spot.id, step = spot.step) {
  ws.unsubscribe(`${id}@snapshot_depth@20@${step}`)
}

const arrow = computed(() => {
  const nv = +trades.value[0]?.price
  const ov = +trades.value[1]?.price
  return nv > ov ? 1 : nv < ov ? -1 : 0
})

function setPanelPrice(value) {
  if (!Number.isNaN(+value)) {
    panel.price = fixRaw(value, n.value)
  }
}
</script>

<template>
  <div>
    <div class="flex items-center justify-between h-[30px] px-3">
      <div class="flex flex-1 gap-1">
        <Ob v-for="i in obs" :key="i.key" v-bind="i" :active="i.key === +obKey" @click="obKey = i.key" />
      </div>
      <div>
        <common-x-n-select v-model:value="spot.step" :options="steps" size="tiny" :show-checkmark="false" :consistent-menu-width="false" />
      </div>
    </div>
    <div class="pb-3">
      <div class="w-full h-full text-end space-y-0.5 text-xs font-medium">
        <div class="flex items-start h-[22px] leading-[10px] text-textTertiary text-[10px] px-3">
          <span class="text-start w-[80px]">{{ $t('价格') }}({{ spot.quote }})</span>
          <span class="px-1 w-[88px]">{{ $t('数量') }}({{ spot.base }})</span>
          <span class="w-[88px]">{{ $t('累计数量') }}({{ spot.base }})</span>
        </div>
        <div class="flex flex-col">
          <Mod :list="asks.list || []" :max="asks.max" :side="-1" :dp="n" />
          <div class="flex items-center w-full px-3 font-medium h-9" :class="{ '-mb-0.5': obKey }">
            <em class="inline-flex items-center text-lg" :class="rateClass">
              <span :class="{ 'text-buy': arrow > 0, 'text-sell': arrow < 0 }">{{ priceText }}</span>
              <Arrow :arrow="arrow" />
            </em>
          </div>
          <Mod :list="bids.list" :max="bids.max" :side="1" :dp="n" />
        </div>
      </div>
    </div>
  </div>
</template>
